import { Alert, Box, Snackbar, SnackbarCloseReason, Stack } from "@mui/material"
import {
  ContentCard,
  SegmentedToggle,
  ViraButton,
  ViraText,
  ViraTextField,
} from "@ksana/vira-ui"
import dayjs from "dayjs"
import { FC, useEffect, useState } from "react"
import { BounceLoader } from "react-spinners"
import {
  ClientFragment,
  GetQuestionnaireTakesByUserIdAndDateRangeDocument,
  GetQuestionnaireTakesByUserIdAndDateRangeQuery,
  GetQuestionnaireTakesByUserIdAndDateRangeQueryVariables,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"
import { generatePdf } from "../../utils/generatePdf"
import { colors } from "../../utils/theme"
import {
  QuestionnairesChartData,
  QuestionnaireTakeByUser,
} from "../../utils/types"
import AssessmentTable from "../molecules/AssessmentTable"
import DisabledActionTooltip from "../molecules/DisabledActionTooltip"
import DisplayChart from "../organisms/questionnaires/displayChart"
import DBTDiaryCardOverview from "./DBTDiaryCardOverview"
import ScheduleItemModal from "./ScheduleItemModal"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

const AssessmentOverview: FC<{
  client: ClientFragment
  canSchedule: boolean
}> = ({ client, canSchedule }) => {
  const graphQLClient = useGraphQLClient()
  const [showDiaryCards, setShowDiaryCards] = useState<boolean>(false)
  const [openScheduleModal, setOpenScheduleModal] = useState<boolean>(false)
  const [hasIncompleteQuestionnaires, setHasIncompleteQuestionnaires] =
    useState<boolean>(false)

  const chartData = (data: QuestionnairesChartData) => {
    setHasIncompleteQuestionnaires(data.hasIncompleteQuestionnaires)
  }
  const [openAssessment, setOpenAssessment] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const [startDate, setStartDate] = useState<string>(
    dayjs().subtract(2, "days").format("YYYY-MM-DD")
  )
  const [endDate, setEndDate] = useState<string>(dayjs().format("YYYY-MM-DD"))

  const [exportQuestionnaireTakes, setExportQuestionnaireTakes] =
    useState<QuestionnaireTakeByUser>([])

  const [open, setOpen] = useState(false)
  const { showDBTDiaryFeatures } = useOrganizationFeatureFlags()

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return
    setOpen(false)
  }

  const [message, setMessage] = useState<string>("")

  useEffect(() => {
    const start = dayjs(startDate)
    const end = dayjs(endDate)

    graphQLClient
      .request<
        GetQuestionnaireTakesByUserIdAndDateRangeQuery,
        GetQuestionnaireTakesByUserIdAndDateRangeQueryVariables
      >(GetQuestionnaireTakesByUserIdAndDateRangeDocument, {
        userId: client.id,
        start: start.format("YYYY-MM-DDT00:00:00"),
        end: end.format("YYYY-MM-DDT23:59:59"),
      })
      .then((res) => {
        const takes = res.questionnaire_takes ?? []
        setExportQuestionnaireTakes(takes as QuestionnaireTakeByUser)
      })
      .catch(() => {
        setMessage("Error fetching questionnaire takes")
        setOpen(true)
      })
  }, [client.id, endDate, graphQLClient, startDate])

  const exportToPdf = async () => {
    if (!showDiaryCards) {
      const start = dayjs(startDate)
      const end = dayjs(endDate)
      const diff = end.diff(start, "day")
      if (start && end && start.isAfter(end)) {
        setMessage("Start Date cannot be after End Date")
        setOpen(true)
        return
      }
      if (diff > 2) {
        setMessage("Date Range selected should be within 2 days")
        setOpen(true)
        return
      }
      if (exportQuestionnaireTakes.length === 0) {
        setMessage("No Assessments available for the selected date range")
        setOpen(true)
        return
      }
      setOpenAssessment(true)
    }
    if (showDiaryCards) setOpenAssessment(true)
    setLoading(true)
    await generatePdf(client.last_name || "", "Assessment", showDiaryCards)
    setLoading(false)
    setOpenAssessment(false)
  }

  const loader = (
    <Box
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 5000,
        textAlign: "center",
      }}
      id="spinner"
    >
      <BounceLoader
        color="rgb(117, 145, 228)"
        size={75}
        loading={loading}
        aria-label="Loading Spinner"
        data-testid="loader"
      />
      {loading ? <ViraText tone="subtle">Generating PDF</ViraText> : null}
    </Box>
  )

  return (
    <Stack spacing={3} sx={{ width: "100%" }} id="assessment-accordion">
      <ContentCard
        title="Assessments"
        actions={
          showDBTDiaryFeatures ? (
            <SegmentedToggle
              fullWidth={false}
              value={showDiaryCards ? "diary" : "charts"}
              onChange={(value) => setShowDiaryCards(value === "diary")}
              aria-label="Assessment view"
              options={[
                { value: "charts", label: "Charts" },
                { value: "diary", label: "Diary Cards" },
              ]}
            />
          ) : undefined
        }
      >
        {loader}
        {showDiaryCards ? (
          <DBTDiaryCardOverview client={client} canSchedule={canSchedule} />
        ) : (
          <DisplayChart client={client} chartCallbackData={chartData} />
        )}
      </ContentCard>

      <Snackbar open={open} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          {message}
        </Alert>
      </Snackbar>

      <AssessmentTable
        assessmentData={exportQuestionnaireTakes}
        openAssessment={openAssessment}
      />

      {hasIncompleteQuestionnaires ? (
        <Box
          sx={(theme) => ({
            display: "grid",
            gap: 3,
            justifyItems: "center",
            alignContent: "center",
            minHeight: 380,
            backgroundColor:
              theme.palette.vira?.softSurface ?? colors.primary.cloud_05,
            borderRadius: 1,
            p: 2,
          })}
        >
          <ViraText
            tone="subtle"
            sx={{ fontStyle: "italic", textAlign: "center", maxWidth: 350 }}
          >
            The client has not completed any assessments.
          </ViraText>
          <DisabledActionTooltip
            disabled={!canSchedule}
            text={SCHEDULING_DISABLED_TOOLTIP}
          >
            <ViraButton
              variant="secondary"
              disabled={!canSchedule}
              onClick={() => setOpenScheduleModal(true)}
            >
              Add to Schedule
            </ViraButton>
          </DisabledActionTooltip>
        </Box>
      ) : null}

      <ScheduleItemModal
        client={client}
        open={openScheduleModal}
        initialItemType="assessment"
        onClose={() => setOpenScheduleModal(false)}
        canSchedule={canSchedule}
      />

      <ContentCard title="Export assessments">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
          sx={{ mb: 2 }}
          id="dateRange"
        >
          <ViraText tone="ink" sx={{ fontWeight: 600 }}>
            Date range
          </ViraText>
          <ViraTextField
            type="date"
            label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <ViraTextField
            type="date"
            label="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
        </Stack>
        <ViraButton variant="ghost" onClick={exportToPdf} disabled={loading}>
          Export
        </ViraButton>
      </ContentCard>
    </Stack>
  )
}

export default AssessmentOverview
